import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Route, Truck, MapPin, Weight, DollarSign, Package, ArrowLeft, Play, RefreshCw,
  CheckCircle2, AlertTriangle, Loader2, ClipboardList
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import VendingRouteMap from '../components/VendingRouteMap';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendingRoutingPage() {
  const { format: fc } = useCurrency();
  const navigate = useNavigate();
  const [tab, setTab] = useState('forecast');
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [forecast, setForecast] = useState(null);
  const [coverDays, setCoverDays] = useState(7);
  const [lookback, setLookback] = useState(14);
  const [loading, setLoading] = useState(false);

  const [selectedForKit, setSelectedForKit] = useState(new Set());
  const [routeName, setRouteName] = useState('');
  const [prekits, setPrekits] = useState([]);
  const [activePrekit, setActivePrekit] = useState(null);
  const [generating, setGenerating] = useState(false);

  const loadMachines = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/machines`);
      setMachines(data.items || []);
    } catch (e) { toast.error('Failed to load machines'); }
  }, []);

  const loadPrekits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/routing/prekits`);
      setPrekits(data.items || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadMachines(); loadPrekits(); }, [loadMachines, loadPrekits]);

  const runForecast = useCallback(async (mid) => {
    if (!mid) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/vending/forecast/${mid}`, { params: { lookback_days: lookback, cover_days: coverDays } });
      setForecast(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forecast failed');
    } finally { setLoading(false); }
  }, [coverDays, lookback]);

  useEffect(() => { if (selectedMachine) runForecast(selectedMachine); }, [selectedMachine, coverDays, lookback, runForecast]);

  const toggleKit = (mid) => {
    setSelectedForKit(prev => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid); else next.add(mid);
      return next;
    });
  };

  const generatePrekit = async () => {
    if (selectedForKit.size === 0) { toast.error('Pick at least one machine'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/vending/routing/prekit`, {
        machine_ids: Array.from(selectedForKit),
        cover_days: coverDays,
        route_name: routeName || undefined,
      });
      toast.success(`Pre-kit generated — ${data.total_units} units for ${data.machines.length} machines`);
      setActivePrekit(data);
      setSelectedForKit(new Set());
      setRouteName('');
      loadPrekits();
      setTab('prekit-detail');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generate failed');
    } finally { setGenerating(false); }
  };

  const openPrekit = async (pid) => {
    try {
      const { data } = await axios.get(`${API}/api/vending/routing/prekits/${pid}`);
      setActivePrekit(data);
      setTab('prekit-detail');
    } catch (e) { toast.error('Failed to load pre-kit'); }
  };

  const updateStatus = async (pid, status) => {
    try {
      await axios.post(`${API}/api/vending/routing/prekits/${pid}/status`, { status });
      toast.success(`Status: ${status}`);
      loadPrekits();
      if (activePrekit?.id === pid) setActivePrekit({ ...activePrekit, status });
    } catch (e) { toast.error('Failed to update status'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="routing-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => navigate('/vending')} data-testid="routing-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Fleet
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Vending Fleet</div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Route className="h-6 w-6 text-emerald-600" /> Predictive Routing &amp; Pre-Kitting</h1>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="forecast" data-testid="routing-tab-forecast">Forecast</TabsTrigger>
          <TabsTrigger value="build-kit" data-testid="routing-tab-build">Build Pre-Kit</TabsTrigger>
          <TabsTrigger value="prekits" data-testid="routing-tab-prekits">All Pre-Kits ({prekits.length})</TabsTrigger>
          <TabsTrigger value="map" data-testid="routing-tab-map">Map</TabsTrigger>
          {activePrekit && <TabsTrigger value="prekit-detail" data-testid="routing-tab-detail">Detail</TabsTrigger>}
        </TabsList>

        {/* FORECAST */}
        <TabsContent value="forecast">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Per-Slot Demand Forecast</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <select value={selectedMachine} onChange={(e) => setSelectedMachine(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm bg-white" data-testid="routing-machine-select">
                  <option value="">Pick machine…</option>
                  {machines.map(m => <option key={m.id} value={m.id}>{m.chassis_serial || m.name}</option>)}
                </select>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-medium">Lookback</span>
                  <Input type="number" value={lookback} onChange={(e) => setLookback(parseInt(e.target.value || '14'))} className="w-16 h-9" data-testid="routing-lookback" />
                  <span className="text-gray-medium">days</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-medium">Cover</span>
                  <Input type="number" value={coverDays} onChange={(e) => setCoverDays(parseInt(e.target.value || '7'))} className="w-16 h-9" data-testid="routing-cover" />
                  <span className="text-gray-medium">days</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => runForecast(selectedMachine)} disabled={!selectedMachine || loading} data-testid="routing-refresh">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>

              {forecast && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <MetricCard label="Chassis" value={forecast.chassis_serial} />
                    <MetricCard label="Refill Units Needed" value={forecast.total_refill_units} tone={forecast.total_refill_units > 0 ? 'warn' : 'ok'} />
                    <MetricCard label="Cover Window" value={`${forecast.cover_days}d`} />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Slot</th>
                          <th className="text-right p-2">Current</th>
                          <th className="text-right p-2">Par</th>
                          <th className="text-right p-2">Avg/day</th>
                          <th className="text-right p-2">Expected out</th>
                          <th className="text-right p-2">Projected</th>
                          <th className="text-right p-2">Refill</th>
                          <th className="text-center p-2">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast.slots.map(s => (
                          <tr key={s.slot_code} className="border-b border-gray-100" data-testid={`forecast-row-${s.slot_code}`}>
                            <td className="p-2 font-semibold">{s.slot_code}</td>
                            <td className="p-2 text-right">{s.current_qty}</td>
                            <td className="p-2 text-right">{s.par_level}</td>
                            <td className="p-2 text-right">{s.avg_daily_vends}</td>
                            <td className="p-2 text-right">{s.expected_out_next_period}</td>
                            <td className="p-2 text-right">{s.projected_qty}</td>
                            <td className="p-2 text-right font-bold">{s.refill_qty}</td>
                            <td className="p-2 text-center">
                              <RiskBadge risk={s.risk} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {!forecast && !loading && <p className="text-sm text-gray-medium text-center py-8">Pick a machine to see its demand forecast.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BUILD KIT */}
        <TabsContent value="build-kit">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Select Machines for the Route</CardTitle></CardHeader>
            <CardContent className="p-3 space-y-3">
              <div className="flex flex-wrap gap-2 items-center">
                <Input placeholder="Route name (optional)" value={routeName} onChange={(e) => setRouteName(e.target.value)} className="max-w-xs h-9" data-testid="routing-route-name" />
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-medium">Cover</span>
                  <Input type="number" value={coverDays} onChange={(e) => setCoverDays(parseInt(e.target.value || '7'))} className="w-16 h-9" />
                  <span className="text-gray-medium">days</span>
                </div>
                <Button size="sm" onClick={generatePrekit} disabled={selectedForKit.size === 0 || generating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="routing-generate-prekit">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                  Generate ({selectedForKit.size})
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {machines.map(m => (
                  <button
                    key={m.id}
                    onClick={() => toggleKit(m.id)}
                    data-testid={`kit-machine-${m.id}`}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${selectedForKit.has(m.id) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'}`}
                  >
                    <div className="flex items-center gap-2">
                      {selectedForKit.has(m.id) && <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{m.chassis_serial || m.name}</div>
                        <div className="text-[11px] text-gray-medium truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{m.location_name || 'No location'}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREKITS LIST */}
        <TabsContent value="prekits">
          <Card>
            <CardContent className="p-3 space-y-2">
              {prekits.length === 0 && <p className="text-sm text-gray-medium text-center py-8">No pre-kits yet. Build one in the &quot;Build Pre-Kit&quot; tab.</p>}
              {prekits.map(p => (
                <button key={p.id} onClick={() => openPrekit(p.id)} data-testid={`prekit-item-${p.id}`} className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-sm font-semibold flex items-center gap-2">{p.route_name} <StatusBadge status={p.status} /></div>
                      <div className="text-[11px] text-gray-medium">{p.machines.length} machines · {p.total_units} units · {(p.total_weight_g / 1000).toFixed(1)}kg · ~${p.cash_collect_estimate?.toFixed(2)} cash</div>
                    </div>
                    <div className="text-[10px] text-gray-400">{new Date(p.created_at).toLocaleString()}</div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MAP */}
        <TabsContent value="map">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Optimized Route Map</CardTitle></CardHeader>
            <CardContent className="p-3">
              <p className="text-xs text-gray-medium mb-2">
                Nearest-neighbor stop ordering across the machines selected in &quot;Build Pre-Kit&quot; (or all machines if none selected). Google Maps polyline shows the optimal driving order.
              </p>
              <VendingRouteMap
                machineIds={selectedForKit.size > 0 ? Array.from(selectedForKit) : machines.map(m => m.id)}
                height={520}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREKIT DETAIL */}
        <TabsContent value="prekit-detail">
          {activePrekit && (
            <div className="space-y-3">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-lg flex items-center gap-2"><ClipboardList className="h-5 w-5" /> {activePrekit.route_name} <StatusBadge status={activePrekit.status} /></CardTitle>
                    <div className="flex gap-1">
                      {['kitted', 'in_transit', 'completed', 'cancelled'].map(s => (
                        <Button key={s} size="sm" variant="outline" onClick={() => updateStatus(activePrekit.id, s)} disabled={activePrekit.status === s} data-testid={`prekit-status-${s}`} className="h-7 text-[11px]">{s}</Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    <MetricCard label="Machines" value={activePrekit.machines.length} icon={Truck} />
                    <MetricCard label="Total units" value={activePrekit.total_units} icon={Package} />
                    <MetricCard label="Weight" value={`${(activePrekit.total_weight_g / 1000).toFixed(1)}kg`} icon={Weight} />
                    <MetricCard label="Est. cash" value={fc(activePrekit.cash_collect_estimate || 0)} icon={DollarSign} />
                  </div>

                  <div>
                    <div className="text-xs font-semibold text-gray-medium mb-1">Picklist</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50"><tr><th className="text-left p-2">Product</th><th className="text-left p-2">SKU</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Machines</th></tr></thead>
                        <tbody>
                          {activePrekit.picklist.map(p => (
                            <tr key={p.product_id} className="border-b border-gray-100" data-testid={`picklist-row-${p.product_id}`}>
                              <td className="p-2">{p.name}</td>
                              <td className="p-2 text-gray-medium">{p.sku || '—'}</td>
                              <td className="p-2 text-right font-bold">{p.qty}</td>
                              <td className="p-2 text-right text-[10px] text-gray-medium">{p.machines.length}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-medium mb-1">Stops</div>
                    <div className="space-y-1">
                      {activePrekit.machines.map(m => (
                        <div key={m.machine_id} className="flex items-center justify-between p-2 rounded border border-gray-200 text-xs">
                          <div><MapPin className="h-3 w-3 inline mr-1" />{m.chassis_serial} · {m.location_name || 'No location'}</div>
                          <span className="text-gray-medium">{m.total_refill_units} units</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({ label, value, tone = 'ok', icon: Icon }) {
  const toneMap = { ok: 'bg-emerald-50 border-emerald-200 text-emerald-700', warn: 'bg-amber-50 border-amber-200 text-amber-700', danger: 'bg-rose-50 border-rose-200 text-rose-700' };
  return (
    <div className={`p-2 rounded-lg border ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className="text-base font-bold">{value}</div>
    </div>
  );
}

function RiskBadge({ risk }) {
  if (risk === 'empty_soon') return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold"><AlertTriangle className="h-2.5 w-2.5" />Empty soon</span>;
  if (risk === 'low') return <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">Low</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">OK</span>;
}

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-gray-100 text-gray-700',
    kitted: 'bg-sky-100 text-sky-700',
    in_transit: 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-rose-100 text-rose-700',
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${map[status] || 'bg-gray-100'}`}>{status}</span>;
}
